import { useEffect, useId, useRef, useState, type PointerEvent } from "react";
import clsx from "clsx";
import type { RoadmapItem, RoadmapItemUpdate, RoadmapTopic } from "../types";
import { roadmapStatuses } from "../utils/roadmapStatus";
import { quarterDates } from "../utils/roadmapQuarters";

const dayMs = 86400000;
const dayNumber = (date: string) => Date.parse(`${date}T00:00:00Z`) / dayMs;
const dateString = (day: number) =>
  new Date(day * dayMs).toISOString().slice(0, 10);
const firstDay = dayNumber("0001-01-01");
const lastDay = dayNumber("9999-12-31");
const weekday = (day: number) => (new Date(day * dayMs).getUTCDay() + 6) % 7;
const shortDate = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

function isoWeek(day: number) {
  const thursday = day + 3 - weekday(day);
  const year = new Date(thursday * dayMs).getUTCFullYear();
  const january4 = new Date(0);
  january4.setUTCFullYear(year, 0, 4);
  const first = january4.getTime() / dayMs;
  return Math.floor((day - (first - weekday(first))) / 7) + 1;
}
const topicColors = [
  "fill-amber-50 stroke-amber-400 dark:fill-amber-950 dark:stroke-amber-500",
  "fill-violet-50 stroke-violet-400 dark:fill-violet-950 dark:stroke-violet-500",
  "fill-teal-50 stroke-teal-400 dark:fill-teal-950 dark:stroke-teal-500",
  "fill-lime-50 stroke-lime-400 dark:fill-lime-950 dark:stroke-lime-500",
  "fill-orange-50 stroke-orange-400 dark:fill-orange-950 dark:stroke-orange-500",
  "fill-primary-50 stroke-primary-400 dark:fill-primary-900 dark:stroke-primary-500",
];
type DragMode = "move" | "start" | "end";
interface RoadmapTimelineProps {
  year: number;
  startQuarter: number;
  endQuarter: number;
  topics: RoadmapTopic[];
  items: RoadmapItem[];
  canEdit: boolean;
  busy: boolean;
  onOpen: (item: RoadmapItem) => void;
  onUpdate: (id: string, payload: RoadmapItemUpdate) => void;
}
interface TimelineDrag {
  item: RoadmapItem;
  mode: DragMode;
  clientX: number;
  pixelsPerDay: number;
  delta: number;
}

function changedDates(item: RoadmapItem, mode: DragMode, delta: number) {
  let start = dayNumber(item.startDate);
  let end = dayNumber(item.endDate);
  if (mode === "move") {
    const shift = Math.max(firstDay - start, Math.min(lastDay - end, delta));
    start += shift;
    end += shift;
  } else if (mode === "start") {
    start = Math.max(firstDay, Math.min(end, start + delta));
  } else {
    end = Math.min(lastDay, Math.max(start, end + delta));
  }
  return { startDate: dateString(start), endDate: dateString(end) };
}

export default function RoadmapTimeline({
  year,
  startQuarter,
  endQuarter,
  topics,
  items,
  canEdit,
  busy,
  onOpen,
  onUpdate,
}: RoadmapTimelineProps) {
  const [drag, setDrag] = useState<TimelineDrag | null>(null);
  const [today, setToday] = useState(() => new Date());
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const now = new Date();
      const midnight = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + 1,
      );
      timer = setTimeout(() => {
        setToday(new Date());
        schedule();
      }, midnight.getTime() - now.getTime());
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);
  const containerRef = useRef<HTMLDivElement>(null);
  const topicRef = useRef<HTMLSpanElement>(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry)
        setAvailableWidth(
          Math.max(
            0,
            entry.contentRect.width -
              (topicRef.current?.getBoundingClientRect().width ?? 0),
          ),
        );
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  const suppressClick = useRef(false);
  const statusId = useId();
  const range = quarterDates(year, startQuarter, endQuarter);
  const yearStart = dayNumber(range.startDate);
  const yearEnd = dayNumber(range.endDate) + 1;
  const chartWidth = Math.max(
    (endQuarter - startQuarter + 1) * 900,
    availableWidth,
  );
  const yearDays = yearEnd - yearStart;
  const x = (day: number) => ((day - yearStart) / yearDays) * chartWidth;
  const todayDate = `${String(today.getFullYear()).padStart(4, "0")}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const todayDay = dayNumber(todayDate);
  const todayX =
    todayDay >= yearStart && todayDay < yearEnd ? x(todayDay) : null;
  const todayMarker = (height: number, firstRow: boolean) =>
    todayX !== null && (
      <g
        role="img"
        aria-label={`Today: ${todayDate}`}
        className="pointer-events-none"
      >
        <title>{`Today: ${todayDate}`}</title>
        <line
          x1={todayX}
          x2={todayX}
          y1={firstRow ? 4 : 0}
          y2={height}
          strokeOpacity={1}
          shapeRendering="crispEdges"
          className="stroke-primary-600 stroke-2 opacity-100 dark:stroke-primary-600"
        />
        {firstRow && (
          <circle
            cx={todayX}
            cy={4}
            r={4}
            className="fill-primary-600 dark:fill-primary-600"
          />
        )}
      </g>
    );
  const weeks: { start: number; end: number; label: string; dates: string }[] =
    [];
  let monday = yearStart - weekday(yearStart);
  if (isoWeek(monday) % 2 === 0) monday -= 7;
  while (monday < yearEnd) {
    const week = isoWeek(monday);
    const days = week === 53 ? 7 : 14;
    weeks.push({
      start: monday,
      end: monday + days,
      label: days === 7 ? `Week ${week}` : `Week ${week} - ${week + 1}`,
      dates: `${shortDate.format(new Date(monday * dayMs))} - ${shortDate.format(new Date((monday + days - 3) * dayMs))}`,
    });
    monday += days;
  }
  const quarters = [0, 3, 6, 9]
    .map((month, i) => ({
      label: `Q${i + 1}`,
      day: dayNumber(
        `${String(year).padStart(4, "0")}-${String(month + 1).padStart(2, "0")}-01`,
      ),
    }))
    .filter((quarter) => quarter.day >= yearStart && quarter.day < yearEnd);
  const begin = (
    event: PointerEvent<SVGRectElement>,
    item: RoadmapItem,
    mode: DragMode,
  ) => {
    suppressClick.current = false;
    if (!canEdit || busy || event.button !== 0) return;
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDrag({
      item,
      mode,
      clientX: event.clientX,
      pixelsPerDay: svg.getBoundingClientRect().width / yearDays,
      delta: 0,
    });
  };
  const deltaFor = (
    event: PointerEvent<SVGRectElement>,
    current: TimelineDrag,
  ) => Math.round((event.clientX - current.clientX) / current.pixelsPerDay);
  const move = (event: PointerEvent<SVGRectElement>) => {
    if (!drag) return;
    const delta = deltaFor(event, drag);
    if (Math.abs(event.clientX - drag.clientX) >= 3)
      suppressClick.current = true;
    setDrag({ ...drag, delta });
  };
  const finish = (event: PointerEvent<SVGRectElement>) => {
    if (!drag) return;
    const delta = deltaFor(event, drag);
    const payload = changedDates(drag.item, drag.mode, delta);
    setDrag(null);
    if (
      payload.startDate !== drag.item.startDate ||
      payload.endDate !== drag.item.endDate
    ) {
      suppressClick.current = true;
      onUpdate(drag.item.id, payload);
    }
  };
  const keyboardShift = (item: RoadmapItem, mode: DragMode, delta: number) => {
    if (!canEdit || busy) return;
    const payload = changedDates(item, mode, delta);
    if (
      payload.startDate !== item.startDate ||
      payload.endDate !== item.endDate
    )
      onUpdate(item.id, payload);
  };
  const grid = (height: number) => (
    <>
      {weeks.map((week, index) => (
        <g key={week.start}>
          <rect
            x={x(Math.max(yearStart, week.start))}
            y={0}
            width={
              x(Math.min(yearEnd, week.end)) -
              x(Math.max(yearStart, week.start))
            }
            height={height}
            className={
              index % 2 === 0
                ? "fill-gray-200/60 dark:fill-gray-700/60"
                : "fill-gray-100/60 dark:fill-gray-800/60"
            }
          />
          <line
            x1={x(Math.max(yearStart, week.start))}
            x2={x(Math.max(yearStart, week.start))}
            y1={0}
            y2={height}
            className="stroke-gray-300 dark:stroke-gray-600"
          />
        </g>
      ))}
      {quarters.map((quarter) => (
        <line
          key={quarter.label}
          x1={x(quarter.day)}
          x2={x(quarter.day)}
          y1={0}
          y2={height}
          className="stroke-gray-400 dark:stroke-gray-500"
        />
      ))}
    </>
  );
  return (
    <section aria-label="Roadmap timeline" className="card space-y-3">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {canEdit
          ? "Click the project to edit its details. Drag to reschedule, or drag either end to adjust its duration."
          : "Select a project to view its details."}
      </p>
      <ul
        aria-label="Project statuses"
        className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600 dark:text-gray-300"
      >
        {Object.entries(roadmapStatuses).map(([value, status]) => (
          <li key={value} className="flex items-center gap-1.5">
            <svg
              aria-hidden="true"
              className={`h-3 w-3 ${status.dot}`}
              viewBox="0 0 12 12"
            >
              <circle cx={6} cy={6} r={4} />
            </svg>
            {status.label}
          </li>
        ))}
      </ul>
      <div
        ref={containerRef}
        className="overflow-x-auto rounded-lg border border-gray-300 dark:border-gray-600"
      >
        <div className="min-w-max">
          <div className="flex items-center border-b border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-900">
            <span
              ref={topicRef}
              className="sticky left-0 z-10 flex h-20 w-40 shrink-0 items-center border-r border-gray-300 bg-gray-50 px-3 text-sm font-semibold text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300"
            >
              Topic
            </span>
            <svg
              width={chartWidth}
              height={80}
              className="block shrink-0"
              aria-label={`Quarter and two-week grid for ${year}`}
            >
              {grid(80)}
              {quarters.map((quarter, i) => (
                <text
                  key={quarter.label}
                  x={(x(quarter.day) + x(quarters[i + 1]?.day ?? yearEnd)) / 2}
                  y={22}
                  textAnchor="middle"
                  className="fill-gray-700 text-sm font-semibold dark:fill-gray-300"
                >
                  {quarter.label}
                </text>
              ))}
              {weeks.map((week) => {
                if (
                  Math.min(yearEnd, week.end) -
                    Math.max(yearStart, week.start) <
                  7
                )
                  return null;
                const center =
                  (x(Math.max(yearStart, week.start)) +
                    x(Math.min(yearEnd, week.end))) /
                  2;
                return (
                  <text
                    key={week.start}
                    x={center}
                    y={47}
                    textAnchor="middle"
                    className="fill-gray-500 text-xs dark:fill-gray-400"
                  >
                    <tspan x={center}>{week.label}</tspan>
                    <tspan x={center} dy={17}>
                      {week.dates}
                    </tspan>
                  </text>
                );
              })}
            </svg>
          </div>
          {topics.map((topic, topicIndex) => {
            const colorIndex =
              Array.from(topic.id).reduce(
                (hash, character) =>
                  (hash * 31 + character.charCodeAt(0)) >>> 0,
                0,
              ) % topicColors.length;
            const color = topicColors[colorIndex];
            const rowEnds: string[] = [];
            const topicItems = items
              .filter(
                (item) =>
                  item.topicId === topic.id &&
                  item.startDate <= range.endDate &&
                  item.endDate >= range.startDate,
              )
              .sort(
                (a, b) =>
                  a.startDate.localeCompare(b.startDate) ||
                  a.endDate.localeCompare(b.endDate) ||
                  a.id.localeCompare(b.id),
              )
              .map((item) => {
                let row = rowEnds.findIndex((end) => end < item.startDate);
                if (row === -1) row = rowEnds.length;
                rowEnds[row] = item.endDate;
                return { item, row };
              });
            const height = Math.max(1, rowEnds.length) * 56;
            return (
              <section
                key={topic.id}
                aria-label={topic.name}
                className="flex border-b border-gray-300 last:border-b-0 dark:border-gray-600"
              >
                <h3 className="sticky left-0 z-10 flex w-40 shrink-0 items-center break-words border-r border-gray-300 bg-gray-50 px-3 py-4 text-sm font-semibold text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100">
                  {topic.name}
                </h3>
                <svg
                  width={chartWidth}
                  height={height}
                  className="block shrink-0"
                  aria-label={`${topic.name} items`}
                >
                  {grid(height)}
                  {topicItems.length === 0 && (
                    <text
                      x={12}
                      y={32}
                      className="fill-gray-500 text-xs dark:fill-gray-400"
                    >
                      No items scheduled
                    </text>
                  )}
                  {topicItems.map(({ item, row }) => {
                    const dates =
                      drag?.item.id === item.id
                        ? changedDates(item, drag.mode, drag.delta)
                        : item;
                    const start = dayNumber(dates.startDate),
                      end = dayNumber(dates.endDate) + 1;
                    const left = x(
                      Math.max(yearStart, Math.min(yearEnd, start)),
                    );
                    const right = x(
                      Math.max(yearStart, Math.min(yearEnd, end)),
                    );
                    const width = Math.max(2, right - left);
                    const y = row * 56 + 8;
                    return (
                      <g key={item.id}>
                        <rect
                          x={left}
                          y={y}
                          width={width}
                          height={36}
                          rx={5}
                          role="button"
                          tabIndex={busy ? -1 : 0}
                          aria-label={`Open ${item.title}`}
                          aria-describedby={`${statusId}-roadmap-status-${item.id}`}
                          aria-disabled={busy}
                          className={clsx(
                            "stroke-2 focus:stroke-primary-900 dark:focus:stroke-primary-300",
                            color,
                            canEdit
                              ? "cursor-grab touch-none"
                              : "cursor-pointer",
                          )}
                          onPointerDown={(event) => begin(event, item, "move")}
                          onPointerMove={move}
                          onPointerUp={finish}
                          onPointerCancel={() => {
                            suppressClick.current = true;
                            setDrag(null);
                          }}
                          onLostPointerCapture={() => setDrag(null)}
                          onClick={() => {
                            if (suppressClick.current) {
                              suppressClick.current = false;
                              return;
                            }
                            if (!busy) onOpen(item);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Escape") {
                              setDrag(null);
                              return;
                            }
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              if (!busy) onOpen(item);
                            }
                            if (
                              !event.shiftKey &&
                              (event.key === "ArrowLeft" ||
                                event.key === "ArrowRight")
                            ) {
                              event.preventDefault();
                              keyboardShift(
                                item,
                                "move",
                                event.key === "ArrowLeft" ? -1 : 1,
                              );
                            }
                          }}
                        >
                          <title>{`${item.title}\nStatus: ${roadmapStatuses[item.status].label}\n${dates.startDate} to ${dates.endDate}\n${item.description}`}</title>
                        </rect>
                        <desc id={`${statusId}-roadmap-status-${item.id}`}>
                          Status: {roadmapStatuses[item.status].label}
                        </desc>
                        <circle
                          cx={left + Math.min(14, width / 2)}
                          cy={y + 18}
                          r={Math.min(4, width / 4)}
                          aria-hidden="true"
                          className={`pointer-events-none ${roadmapStatuses[item.status].dot}`}
                        />
                        {width >= 45 && (
                          <text
                            x={left + 24}
                            y={y + 23}
                            className="pointer-events-none fill-gray-900 text-xs font-medium dark:fill-gray-100"
                          >
                            {item.title.length >
                            Math.max(0, Math.floor((width - 36) / 7))
                              ? `${item.title.slice(0, Math.max(0, Math.floor((width - 36) / 7) - 3))}...`
                              : item.title}
                          </text>
                        )}
                        <text className="sr-only">
                          {dates.startDate} to {dates.endDate}
                        </text>
                        {canEdit &&
                          (["start", "end"] as const).map((mode) => {
                            if (
                              (mode === "start" && start < yearStart) ||
                              (mode === "end" && end > yearEnd)
                            )
                              return null;
                            return (
                              <rect
                                key={mode}
                                x={
                                  mode === "start"
                                    ? left
                                    : Math.max(left, right - 7)
                                }
                                y={width < 20 ? y + 26 : y}
                                width={7}
                                height={width < 20 ? 10 : 36}
                                rx={2}
                                role="button"
                                tabIndex={busy ? -1 : 0}
                                aria-disabled={busy}
                                aria-label={`Resize ${mode} of ${item.title}`}
                                className={clsx(
                                  "cursor-ew-resize touch-none stroke-2 focus:stroke-primary-900 dark:focus:stroke-primary-300",
                                  color,
                                )}
                                onPointerDown={(event) =>
                                  begin(event, item, mode)
                                }
                                onPointerMove={move}
                                onPointerUp={finish}
                                onPointerCancel={() => setDrag(null)}
                                onLostPointerCapture={() => setDrag(null)}
                                onKeyDown={(event) => {
                                  if (event.key === "Escape") setDrag(null);
                                  if (
                                    !event.shiftKey &&
                                    (event.key === "ArrowLeft" ||
                                      event.key === "ArrowRight")
                                  ) {
                                    event.preventDefault();
                                    keyboardShift(
                                      item,
                                      mode,
                                      event.key === "ArrowLeft" ? -1 : 1,
                                    );
                                  }
                                }}
                              />
                            );
                          })}
                      </g>
                    );
                  })}
                  {todayMarker(height, topicIndex === 0)}
                </svg>
              </section>
            );
          })}
        </div>
      </div>
    </section>
  );
}
